import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";


const sliderData = [
  {
    image: "/images/slide1.jpg",
    title: "Expert Framing Solutions",
    subtitle: "Crafting the foundation of your dreams with precision and care",
    cta: "Get Free Quote"
  },
  {
    image: "/images/slide2.jpg", 
    title: "Quality Craftsmanship",
    subtitle: "Every beam, every joint, built to stand the test of time",
    cta: "View Our Work"
  },
  {
    image: "/images/slide3.jpg",
    title: "Professional Excellence",
    subtitle: "Trusted by homeowners and builders for over 20 years",
    cta: "Meet Our Team"
  }
];

const services = [
  {
    icon: "🏠",
    title: "Residential Framing",
    description: "Custom framing solutions for new homes and renovations with unmatched precision."
  },
  {
    icon: "🏢", 
    title: "Commercial Framing",
    description: "Structural excellence for offices, retail spaces, and commercial buildings."
  },
  {
    icon: "🔨",
    title: "Custom Wood Structures",
    description: "Specialty framing including pergolas, decks, and unique architectural elements."
  },
  {
    icon: "📐",
    title: "Project Consulting",
    description: "Expert planning and consultation for your framing and construction projects."
  }
];

const blogPosts = [
  {
    id: "choosing-framing-contractor",
    image: "/images/blog-choosing-contractor.jpg",
    title: "How to Choose the Right Framing Contractor",
    excerpt: "Essential tips for selecting a reliable and skilled framing contractor for your project.",
    date: "March 15, 2024",
    readTime: "5 min read"
  },
  {
    id: "framing-safety-practices",
    image: "/images/blog-safety-practices.jpg",
    title: "Framing Safety Best Practices",
    excerpt: "Important safety guidelines and best practices for construction framing projects.",
    date: "March 10, 2024", 
    readTime: "4 min read"
  },
  {
    id: "luxury-home-build",
    image: "/images/blog-luxury-home.jpg",
    title: "Building a Luxury Home: Framing Essentials",
    excerpt: "Discover the unique framing requirements and best practices for luxury home construction.",
    date: "March 5, 2024",
    readTime: "6 min read"
  }
];

const projects = [
  {
    image: "/images/project-luxury-home.jpg",
    title: "Modern Luxury Home",
    category: "Residential",
    description: "Contemporary framing for a stunning 5,000 sq ft luxury residence"
  },
  {
    image: "/images/project-office-complex.jpg", 
    title: "Downtown Office Complex",
    category: "Commercial",
    description: "Multi-story commercial framing with modern architectural elements"
  },
  {
    image: "/images/project-deck-pergola.jpg",
    title: "Custom Deck & Pergola",
    category: "Custom",
    description: "Handcrafted outdoor living space with premium wood framing"
  },
  {
    image: "/images/project-retail-space.jpg",
    title: "Retail Space Framing",
    category: "Commercial",
    description: "Efficient framing solution for modern retail environment"
  },
  {
    image: "/images/project-home-addition.jpg",
    title: "Traditional Home Addition",
    category: "Residential",
    description: "Seamless integration of new framing with existing structure"
  }
];

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentProject, setCurrentProject] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderData.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % sliderData.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + sliderData.length) % sliderData.length);

  const nextProject = () => setCurrentProject((prev) => (prev + 1) % projects.length);
  const prevProject = () => setCurrentProject((prev) => (prev - 1 + projects.length) % projects.length);

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Low Cost Framing by Woodhenge Builders | Expert Rough Framing Services</title>
        <meta name="description" content="Professional rough framing services for residential and commercial projects. Quality craftsmanship, expert team, and reliable solutions. Backed by Woodhenge Builders' 20+ years of expertise." />
        <meta name="keywords" content="rough framing, framing contractor, residential framing, commercial framing, wood framing, construction framing, framing services, Orange County framing" />
        <meta property="og:title" content="Low Cost Framing by Woodhenge Builders | Expert Rough Framing Services" />
        <meta property="og:description" content="Professional rough framing services for residential and commercial projects. Quality craftsmanship, expert team, and reliable solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="http://www.lowcostframing.com" />
        <link rel="canonical" href="http://www.lowcostframing.com" />
      </Helmet>

      {/* Hero Slider/Carousel */}
      <section className="relative h-screen overflow-hidden">
        {sliderData.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white max-w-5xl mx-auto px-6">
                <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                  {slide.title}
                </h1>
                <p className="text-xl md:text-2xl mb-10 opacity-90 font-light text-amber-100">
                  {slide.subtitle}
                </p>
                <Link to="/contact">
                  <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                    Get Free Quote
                  </button>
                </Link>
              </div>
            </div>
          </div>
        ))}
        
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-4 transition-all duration-300 backdrop-blur-sm border border-white/20"
          aria-label="Previous slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-4 transition-all duration-300 backdrop-blur-sm border border-white/20"
          aria-label="Next slide"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
          {sliderData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                index === currentSlide ? "bg-amber-600 border-amber-600 scale-125" : "bg-transparent border-white/50 hover:border-white"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>



      {/* Services Section */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Our Services
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional framing solutions crafted with precision and expertise
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-10 shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-300 leading-relaxed text-lg">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link to="/services">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Latest Insights
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Stay informed with industry expertise and project highlights
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <article
                key={index}
                className="bg-gray-900 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="h-48 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${post.image})` }}>
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-400 mb-4">
                    <span>{post.date}</span>
                    <span className="mx-2">•</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <Link to={`/blog/${post.id}`} className="text-amber-500 hover:text-amber-400 font-semibold transition-colors group-hover:translate-x-2 inline-block">
                    Read More →
                  </Link>
                </div>
              </article>
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link to="/blog">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                View All Posts
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Projects Carousel */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Featured Projects
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Showcasing our finest work and exceptional craftsmanship
            </p>
          </div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className="overflow-hidden rounded-2xl shadow-2xl">
              <div className="flex transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${currentProject * 100}%)` }}>
                {projects.map((project, index) => (
                  <div key={index} className="w-full flex-shrink-0 relative">
                    <div 
                      className="h-[500px] bg-cover bg-center bg-no-repeat"
                      style={{ backgroundImage: `url(${project.image})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                      <div className="absolute bottom-12 left-12 right-12 text-white">
                        <span className="bg-amber-800 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 inline-block border border-amber-600">
                          {project.category}
                        </span>
                        <h3 className="text-3xl font-bold mb-3">{project.title}</h3>
                        <p className="text-gray-200 text-lg max-w-md">{project.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <button
              onClick={prevProject}
              className="absolute left-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Previous project"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextProject}
              className="absolute right-6 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-4 shadow-2xl transition-all duration-300 border border-white/20 backdrop-blur-sm"
              aria-label="Next project"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          
          <div className="flex justify-center mt-12 space-x-4">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentProject(index)}
                className={`w-4 h-4 rounded-full transition-all duration-300 border-2 ${
                  index === currentProject ? "bg-amber-600 border-amber-600 scale-125" : "bg-transparent border-gray-400 hover:border-white"
                }`}
                aria-label={`Go to project ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-20 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div>
              <h3 className="text-2xl font-bold text-amber-400 mb-6">Rough Framing Co.</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Your trusted partner for quality framing solutions. We bring expertise, 
                reliability, and craftsmanship to every project.
              </p>
              <div className="flex space-x-4">
                <button className="text-gray-400 hover:text-amber-400 transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </button>
                <button className="text-gray-400 hover:text-amber-400 transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </button>
                <button className="text-gray-400 hover:text-amber-400 transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-bold text-amber-400 mb-6">Contact Information</h4>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href="tel:+1234567890" className="hover:text-amber-400 transition-colors">(123) 456-7890</a>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href="mailto:info@roughframing.com" className="hover:text-amber-400 transition-colors">info@roughframing.com</a>
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>123 Main Street, City, State 12345</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xl font-bold text-amber-400 mb-6">Quick Links</h4>
              <ul className="space-y-3 text-gray-300">
                <li><Link to="/services" className="hover:text-amber-400 transition-colors">Services</Link></li>
                <li><Link to="/about" className="hover:text-amber-400 transition-colors">About Us</Link></li>
                <li><Link to="/projects" className="hover:text-amber-400 transition-colors">Projects</Link></li>
                <li><Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-amber-400 transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xl font-bold text-amber-400 mb-6">Business Hours</h4>
              <div className="space-y-2 text-gray-300">
                <div>Monday - Friday: 7:00 AM - 5:00 PM</div>
                <div>Saturday: 8:00 AM - 3:00 PM</div>
                <div>Sunday: Closed</div>
              </div>
            </div>
          </div>

          <div className="text-center border-t border-gray-800 pt-16">
            <h3 className="text-4xl font-bold mb-6">Ready to Start Your Project?</h3>
            <p className="text-gray-300 mb-10 max-w-3xl mx-auto text-lg leading-relaxed">
              Get a free estimate and consultation from our expert team. 
              We're here to bring your vision to life with quality craftsmanship.
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-5 px-12 rounded-lg text-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Request a Free Quote
              </button>
            </Link>
          </div>

          <div className="text-center text-gray-400 text-sm mt-16 pt-8 border-t border-gray-800">
            <p>&copy; {new Date().getFullYear()} Rough Framing Co. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home; 